import { useMemo } from 'react';
import { Menu, Dropdown } from 'antd';
import { FileAddFilled } from '@ant-design/icons';
import Messages from 'i18n/messages/home';

import FavoriteControl from './FavoriteControl';
import UnfavoriteControl from './UnfavoriteControl';
import AddFolder from './AddFolder';
import AddFolderToNewAPL from './AddFolderToNewAPL';
import AddProductToNewApl from './AddProductToNewApl';
import AddSearchProductToNewApl from './AddSearchProductToNewApl';
import AddSearchProductToFolder from './AddSearchProductToFolder';
import RibbonButton from 'common/components/button/RibbonButton';

import { Can } from 'context/Can';
import { useCheckPermissions } from 'hooks/useCheckPermissions';
import { ABILITY_ACTION, ABILITY_SUBJECT } from 'static/Permission';

import { CanShow } from 'common/components/wrapper/show-condition';

import { useGetMemberHeader } from 'common/queries/member-module';
import { useGetMemberId } from 'hooks';

import './Addition.less';

import { useUserInfo } from 'hooks/auth/useUserInfo';

const Addition = ({
  type,
  selectedItemList,
  disabledFavorite,
  isDisabled,
  disabledFilterSearch,
  searchCategoriesEffected,
  searchText,
  filterProducts,
  queryConditions,
  isFavoriteView,
  isOverlay,
  gridFilters,
}) => {
  let { userInfo } = useUserInfo();
  const { memberId } = useGetMemberId();
  const { data: memberInfo } = useGetMemberHeader(
    {
      memberId,
    },
    {
      enabled: Boolean(memberId && !isDisabled),
    }
  );

  const checkAllFavoriteItem = () => {
    // return true if all item is favorite
    return !selectedItemList?.some((item) => !item?.isFavorited);
  };

  const checkAllNotFavoriteItem = () => {
    // return true if all item is not favorite
    return !selectedItemList?.some((item) => item?.isFavorited);
  };

  const shouldShowRemoveFavorite =
    (!selectedItemList && memberInfo?.isFavorited) ||
    (selectedItemList?.length > 0 && checkAllFavoriteItem());

  const isAllItemInTheSameType = selectedItemList?.every(
    (item) => item?.type?.toLowerCase() === type?.toLowerCase()
  );

  const shouldDisabledAddToFavorite =
    (!selectedItemList && memberInfo?.isFavorited) ||
    selectedItemList?.length === 0 ||
    !checkAllNotFavoriteItem() ||
    disabledFavorite ||
    !isAllItemInTheSameType;

  const viewPermission = useMemo(() => getPermissionAddToFav(type), [type]);

  const hasPermissionAddToFavorite = useCheckPermissions(viewPermission);

  const menuOptions = (
    <Menu>
      {hasPermissionAddToFavorite && !isOverlay && (
        <Menu.Item key='favorite' style={{ backgroundColor: 'inherit' }}>
          <FavoriteControl
            type={type}
            userId={userInfo?.id}
            selectedItemList={selectedItemList}
            isDisabled={shouldDisabledAddToFavorite}
          />
        </Menu.Item>
      )}

      {hasPermissionAddToFavorite && shouldShowRemoveFavorite && (
        <Menu.Item key='unfavorite' style={{ backgroundColor: 'inherit' }}>
          <UnfavoriteControl
            type={type}
            selectedItemList={selectedItemList}
            userId={userInfo?.id}
            isFavoriteView={isFavoriteView}
          />
        </Menu.Item>
      )}
      <Menu.Item key='addToFolder' style={{ backgroundColor: 'inherit' }}>
        <Can I={ABILITY_ACTION.VIEW} a={ABILITY_SUBJECT.FOLDER}>
          <AddFolder selectedItemList={selectedItemList} />
        </Can>
      </Menu.Item>
      {type === 'folder' && (
        <Menu.Item key='addToNewAPL' style={{ backgroundColor: 'inherit' }}>
          <AddFolderToNewAPL />
        </Menu.Item>
      )}
      {type === 'product' && !isOverlay && (
        <CanShow.SharedOnlyMember>
          <Menu.Item key='addToApl' style={{ backgroundColor: 'inherit' }}>
            <AddProductToNewApl selectedItemList={selectedItemList} />
          </Menu.Item>
        </CanShow.SharedOnlyMember>
      )}
      {type === 'product' && !isOverlay && (
        <CanShow.SharedOnlyMember>
          <Menu.Item
            key='addSearchProductToNewApl'
            style={{ backgroundColor: 'inherit' }}
          >
            <AddSearchProductToNewApl
              disabledFilterSearch={disabledFilterSearch}
              searchCategoriesEffected={searchCategoriesEffected}
              searchText={searchText}
              filterProducts={filterProducts}
              queryConditions={queryConditions}
              gridFilters={gridFilters}
            />
          </Menu.Item>
        </CanShow.SharedOnlyMember>
      )}
      {type === 'product' && !isOverlay && (
        <Can I={ABILITY_ACTION.VIEW} a={ABILITY_SUBJECT.FOLDER}>
          <Menu.Item
            key='addSearchProductToFolder'
            style={{ backgroundColor: 'inherit' }}
          >
            <AddSearchProductToFolder
              disabledFilterSearch={disabledFilterSearch}
            />
          </Menu.Item>
        </Can>
      )}
    </Menu>
  );

  return (
    <Dropdown
      overlay={menuOptions}
      placement='bottomLeft'
      disabled={
        window.location.pathname === '/member-management-company' || isDisabled
      }
      arrow
    >
      <RibbonButton
        icon={<FileAddFilled />}
        disabled={
          window.location.pathname === '/member-management-company' ||
          isDisabled
        }
        label={Messages.addTo}
      />
    </Dropdown>
  );
};

const getPermissionAddToFav = (type) => {
  if (type === 'product') {
    return [
      {
        action: ABILITY_ACTION.VIEW,
        subject: ABILITY_SUBJECT.PRODUCT,
      },
    ];
  }

  if (type === 'member') {
    return [
      {
        action: ABILITY_ACTION.VIEW,
        subject: ABILITY_SUBJECT.MEMBER,
      },
    ];
  }

  if (type === 'asset') {
    return [
      {
        action: ABILITY_ACTION.VIEW,
        subject: ABILITY_SUBJECT.ASSET,
      },
    ];
  }

  if (type === 'folder') {
    return [
      {
        action: ABILITY_ACTION.VIEW,
        subject: ABILITY_SUBJECT.FOLDER,
      },
    ];
  }

  if (type === 'reporting') {
    return [
      {
        action: ABILITY_ACTION.VIEW,
        subject: ABILITY_SUBJECT.REPORTING,
      },
    ];
  }

  return [];
};

export default Addition;
